import { useEffect, useMemo, useState } from 'react'
import { AxiosError } from 'axios'
import { Flex, Spinner, Text } from '@radix-ui/themes'
import { useParams } from '@tanstack/react-router'
import { IconCheck, IconInfoCircle, IconX } from '@tabler/icons-react'
import { useAddClass } from '@/services/addClassService'
import { useGetClassByModules } from '@/services/classService'
import { ClassByModule } from '@/types/class.type'
import { ListOfClass } from '@/types/student.type'
import { toast } from 'sonner'
import { useModules } from '@/stores/useModulesStore'
import { cn } from '@/lib/utils'
import SearchBox from '@/components/ui/SearchBox'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Tooltip,
} from '@/components/ui/tooltip'

type SelectableClass = ClassByModule & Partial<ListOfClass>

const mapListClassToSelectable = (item: ListOfClass): SelectableClass => ({
  id: item.class_id,
  name: item.class_name,
  class_id: item.class_id,
  class_name: item.class_name,
  module_id: item.module_id,
  module_name: item.module_name,
  is_assign: true,
})

const mapModuleClassToSelectable = (item: ClassByModule): SelectableClass => ({
  ...item,
  class_id: item.id,
  class_name: item.name,
})

const AddClassModal = ({
  userName,
  classes,
  isOpen,
  setIsOpen,
  handleRefresh,
}: {
  userName: string
  classes: ListOfClass[]
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  handleRefresh: () => void
}) => {
  const { studentId } = useParams({ strict: false })
  const { modules } = useModules()
  const [step, setStep] = useState(1)
  const [searchModule, setSearchModule] = useState('')
  const [searchClass, setSearchClass] = useState('')
  const [selectedModules, setSelectedModules] = useState<number[]>([])
  const [selectedClasses, setSelectedClasses] = useState<SelectableClass[]>([])
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [isSearchClassFocused, setIsSearchClassFocused] = useState(false)
  const [title, setTitle] = useState('')

  const { classesByModule } = useGetClassByModules({
    userId: studentId as string,
    moduleIds: selectedModules,
  })
  const { addClassAsync, isAddingClass } = useAddClass()

  const assignedClasses = useMemo(() => {
    const map = new Map<number, SelectableClass>()

    classes.forEach((item) => {
      map.set(item.class_id, mapListClassToSelectable(item))
    })

    classesByModule
      ?.filter((item) => item.is_assign)
      .forEach((item) => {
        map.set(item.id, mapModuleClassToSelectable(item))
      })

    return Array.from(map.values())
  }, [classes, classesByModule])

  const assignedClassIds = useMemo(
    () => assignedClasses.map((item) => item.id),
    [assignedClasses]
  )

  const combinedSelectedClassIds = useMemo(() => {
    const selectedIds = selectedClasses.map((item) => item.id)
    return new Set([...selectedIds, ...assignedClassIds])
  }, [selectedClasses, assignedClassIds])

  const displaySelectedClasses = useMemo(() => {
    const map = new Map<number, SelectableClass>()

    assignedClasses.forEach((item) => {
      map.set(item.id, item)
    })

    selectedClasses.forEach((item) => {
      map.set(item.id, item)
    })

    return Array.from(map.values())
  }, [assignedClasses, selectedClasses])

  // todo : func select all

  useEffect(() => {
    switch (step) {
      case 1: {
        setTitle('Existing classes')
        break
      }
      case 2: {
        setTitle('Add new classes')
        break
      }
      case 3: {
        setTitle(
          `Selected classes (${selectedClasses.length + assignedClassIds.length})`
        )
        break
      }
      default:
        setTitle('Existing classes')
    }
  }, [step, selectedClasses.length, assignedClassIds.length])

  const handleNext = () => {
    if (step === 1) {
      setStep(2)
    }

    if (step === 2) {
      setStep(3)
    }

    if (step === 3) {
      handleAddClass()
    }
  }

  const handleBack = () => {
    if (step === 1) {
      setIsOpen(false)
      setStep(1)
      handleClear()
    }
    if (step === 2) {
      setStep(1)
    }
    if (step === 3) {
      setStep(2)
    }
  }

  const handleSelectAllModules = () => {
    const moduleIds = modules.items.map((item) => item.id)
    setSelectedModules(moduleIds)
  }

  const handleDeselectAllModules = () => {
    setSelectedModules([])
  }

  const handleSelectAllClasses = () => {
    if (!classesByModule || classesByModule.length === 0) return

    setSelectedClasses((prev) => {
      const map = new Map<number, SelectableClass>()

      prev.forEach((item) => {
        const key = item.id ?? item.class_id
        if (key !== undefined) {
          map.set(key, item)
        }
      })

      classesByModule.forEach((item) => {
        if (item.is_assign) return
        map.set(item.id, mapModuleClassToSelectable(item))
      })

      return Array.from(map.values())
    })
  }

  const handleDeselectAllClasses = () => {
    // Only clear newly selected classes; assigned classes remain
    setSelectedClasses([])
  }

  const handleClear = () => {
    setSelectedClasses([])
    setSelectedModules([])
  }

  const handleAddClass = () => {
    const ids = assignedClasses
      .concat(selectedClasses)
      .map((item) => item.id ?? item.class_id)

    addClassAsync({ userId: studentId as string, classIds: ids })
      .then(() => {
        toast.success('Add student to classes successfully', {
          duration: 3000,
          position: 'top-right',
        })
        handleClear()
        setStep(1)
        setIsOpen(false)
        handleRefresh()
      })
      .catch((error: AxiosError) => {
        console.log('qualify err', error)
        const errorMsg =
          (error.response?.data as { detail?: string })?.detail ||
          'Failed to upload file'
        toast.error(errorMsg, {
          duration: 3000,
          position: 'top-right',
        })
      })
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(val) => {
        setIsOpen(val)
        setStep(1)
        handleClear()
      }}
    >
      <DialogContent className='max-h-[90vh] gap-2 sm:max-w-[640px]'>
        <DialogHeader className='text-left'>
          <DialogTitle className='text-xl font-normal'>{`Add ${userName} to classes`}</DialogTitle>
        </DialogHeader>
        <div className='flex items-center gap-2 text-lg text-[#002652]'>
          {title}
          {step === 3 && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconInfoCircle className='z-50 min-h-6 min-w-6' />
                </TooltipTrigger>
                <TooltipContent className='max-w-[400px]'>
                  <p className=''>
                    ⚠️ Caution: Users cannot be removed from a class once added.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div>
          {classes.length === 0 && step === 1 && <p>No classes available.</p>}
          {classes.length > 0 && step === 1 && (
            <Flex
              className='max-h-[50vh] overflow-auto pr-1'
              direction={'column'}
            >
              {classes.map((item, index) => {
                // const classImg = modulesImgs.find(
                //   (module) => module.name === item.module_name
                // )?.imgUrl
                return (
                  <div
                    key={item.class_id}
                    className={cn(
                      'relative flex cursor-pointer items-center gap-3 py-4'
                    )}
                  >
                    {/* <img
                      src={classImg ?? studentDefaultAvatar}
                      alt='module'
                      className='h-[40px] w-[40px] rounded-sm object-cover'
                    /> */}
                    <Flex className='min-w-0 flex-1' direction='column' gap='1'>
                      <Text className='text-[16px] font-[400] break-words text-[#002652]'>
                        {item.class_name}
                      </Text>
                    </Flex>
                    {index !== classes.length - 1 && (
                      <div className='absolute bottom-[0px] left-[50%] w-full -translate-x-[50%] border-t-[1px] border-[#EAECF0]'></div>
                    )}
                  </div>
                )
              })}
            </Flex>
          )}

          {step === 2 && (
            <>
              {/* search module container  */}
              <div className='flex flex-col'>
                <div className='mb-1.5 flex w-full items-center justify-between text-sm text-[#344054]'>
                  Filter by Module ({selectedModules.length})
                  <div className='flex items-center gap-2'>
                    <p
                      className='cursor-pointer text-[#438fe6] hover:text-[#95beee]'
                      onClick={() => handleSelectAllModules()}
                    >
                      Select All ({modules.items.length})
                    </p>
                    <p
                      className='cursor-pointer text-[#f14842] hover:text-[#f79f9f]'
                      onClick={handleDeselectAllModules}
                    >
                      Deselect
                    </p>
                  </div>
                </div>

                <div className='relative'>
                  <SearchBox
                    value={searchModule}
                    onValueChange={(value: string) =>
                      setSearchModule(value.trim())
                    }
                    placeHolder={
                      selectedModules.length === 0
                        ? 'Select a module'
                        : 'Select a module'
                    }
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                    selectedItems={
                      selectedModules.length > 0
                        ? selectedModules.map((moduleId) => {
                            const module = modules.items.find(
                              (m) => m.id === moduleId
                            )
                            if (!module) return null
                            return (
                              <div
                                key={moduleId}
                                className='flex max-w-full items-center gap-1 rounded-md bg-[#e9e9e9] px-2 py-1 text-xs text-[#344054]'
                                title={module.name}
                              >
                                <span className='truncate'>{module.name}</span>
                                <button
                                  type='button'
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setSelectedModules((prev) =>
                                      prev.filter((id) => id !== moduleId)
                                    )
                                  }}
                                  className='hover:bg-muted rounded-full p-0.5 transition-colors'
                                  aria-label={`Remove ${module.name}`}
                                  onMouseDown={(e) => e.preventDefault()}
                                >
                                  <IconX className='size-4 text-[#667085]' />
                                </button>
                              </div>
                            )
                          })
                        : undefined
                    }
                  />

                  {isSearchFocused && (
                    <div
                      className='absolute right-0 left-0 z-60 mt-1 max-h-[260px] w-full overflow-auto rounded-xl border border-[#EAECF0] bg-white p-1.5 shadow-lg'
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <div className='flex flex-col items-center justify-center gap-2'>
                        {modules.items
                          .filter((item) =>
                            item.name
                              .toLowerCase()
                              .includes(searchModule.toLowerCase())
                          )
                          .map((item) => {
                            const isSelected = selectedModules.includes(item.id)
                            return (
                              <div
                                key={item.id}
                                className={cn(
                                  'flex w-full cursor-pointer items-start justify-between gap-2 rounded-lg px-2 py-2.5 transition-colors hover:bg-[#c7ddf3]',
                                  isSelected && 'bg-[#c7ddf3]'
                                )}
                                title={item.name}
                                onClick={() =>
                                  setSelectedModules((prev) =>
                                    isSelected
                                      ? prev.filter((id) => id !== item.id)
                                      : [...prev, item.id]
                                  )
                                }
                              >
                                <p className='min-w-0 flex-1 break-words'>
                                  {item.name}
                                </p>
                                {isSelected && (
                                  <IconCheck
                                    className='mt-0.5 size-5 shrink-0'
                                    color='#0057B8'
                                  />
                                )}
                              </div>
                            )
                          })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* search class container  */}

              <div className='mt-3 flex flex-col'>
                <div className='mb-1.5 flex w-full items-center justify-between text-sm text-[#344054]'>
                  <div className='flex items-center gap-3'>
                    <p>
                      Class <span className='text-[#c20303]'>*</span> (
                      {selectedClasses.length + assignedClassIds.length})
                    </p>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <IconInfoCircle className='z-50 min-h-6 min-w-6' />
                        </TooltipTrigger>
                        <TooltipContent className='max-w-[400px]'>
                          <p className=''>
                            ⚠️ Caution: Users cannot be removed from a class
                            once added.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className='flex items-center gap-2'>
                    <p
                      className='cursor-pointer text-[#438fe6] hover:text-[#95beee]'
                      onClick={handleSelectAllClasses}
                    >
                      Select All ({classesByModule?.length})
                    </p>
                    <p
                      className='cursor-pointer text-[#f14842] hover:text-[#f79f9f]'
                      onClick={handleDeselectAllClasses}
                    >
                      Deselect
                    </p>
                  </div>
                </div>
                <div className='relative'>
                  <SearchBox
                    value={searchClass}
                    onValueChange={(value: string) =>
                      setSearchClass(value.trim())
                    }
                    placeHolder={
                      classesByModule?.length === 0
                        ? 'Select a class'
                        : 'Select a class'
                    }
                    onFocus={() => setIsSearchClassFocused(true)}
                    onBlur={() => setIsSearchClassFocused(false)}
                    selectedItems={displaySelectedClasses.map((selected) => (
                      <div
                        key={selected.id}
                        className='flex max-w-full items-center gap-1 rounded-md bg-[#e9e9e9] px-2 py-1 text-xs text-[#344054]'
                        title={selected.name}
                      >
                        <span className='truncate'>{selected.name}</span>
                        {!selected.is_assign && (
                          <button
                            type='button'
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedClasses((prev) =>
                                prev.filter(
                                  (classItem) => classItem.id !== selected.id
                                )
                              )
                            }}
                            className='hover:bg-muted rounded-full p-0.5 transition-colors'
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <IconX className='size-4 text-[#667085]' />
                          </button>
                        )}
                      </div>
                    ))}
                  />

                  {isSearchClassFocused && (
                    <div
                      className='absolute right-0 left-0 z-50 mt-1 max-h-[280px] w-full overflow-auto rounded-xl border border-[#EAECF0] bg-white p-1.5 shadow-lg'
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {classesByModule?.length === 0 && (
                        <p className='text-center text-sm text-[#667085]'>
                          No class available
                        </p>
                      )}
                      {classesByModule && classesByModule?.length > 0 && (
                        <div className='flex flex-col items-center justify-center gap-2'>
                          {classesByModule
                            ?.sort(
                              (a, b) =>
                                Number(b.is_assign) - Number(a.is_assign)
                            )
                            ?.filter((item) =>
                              item.name
                                .toLowerCase()
                                .includes(searchClass.toLowerCase())
                            )
                            .map((item) => {
                              const isSelected = combinedSelectedClassIds.has(
                                item.id
                              )
                              return (
                                <div
                                  key={item.id}
                                  className={cn(
                                    'flex w-full cursor-pointer items-start justify-between gap-2 rounded-lg px-2 py-2.5 transition-colors hover:bg-[#eaebec]',
                                    isSelected && 'bg-[#eaebec]',
                                    item.is_assign && 'cursor-not-allowed'
                                  )}
                                  title={item.name}
                                  onClick={() => {
                                    if (item.is_assign) return
                                    setSelectedClasses((prev) => {
                                      const exists = prev.some(
                                        (selected) => selected.id === item.id
                                      )
                                      if (exists) {
                                        return prev.filter(
                                          (selected) => selected.id !== item.id
                                        )
                                      }
                                      return [...prev, item]
                                    })
                                  }}
                                >
                                  <p className='min-w-0 flex-1 break-words'>
                                    {item.name}
                                  </p>
                                  {isSelected && (
                                    <IconCheck
                                      className='mt-0.5 size-5 shrink-0'
                                      color='#0057B8'
                                    />
                                  )}
                                </div>
                              )
                            })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <Flex
              direction={'column'}
              className='max-h-[50vh] overflow-auto pr-1'
            >
              {assignedClasses?.concat(selectedClasses)?.map((item, index) => {
                const className = item.class_name ?? item.name ?? ''

                const isAssigned = assignedClasses.some(
                  (assigned) =>
                    (assigned.id ?? assigned.class_id) ===
                    (item.id ?? item.class_id)
                )

                return (
                  <div
                    key={item.id ?? item.class_id}
                    className={cn(
                      'relative flex cursor-pointer items-start gap-3 py-4'
                    )}
                  >
                    <p className='shrink-0'>{index + 1}. </p>
                    <Flex
                      className='gap-2'
                      flexGrow={'1'}
                      justify={'between'}
                      align={'start'}
                    >
                      <Text className='min-w-0 flex-1 text-[16px] font-[400] break-words text-[#002652]'>
                        {className}
                      </Text>
                      <button
                        type='button'
                        className={cn(
                          'my-auto size-8 min-w-8 shrink-0 rounded p-1 hover:bg-red-300 disabled:cursor-not-allowed disabled:opacity-50'
                        )}
                        disabled={isAssigned}
                        onClick={(e) => {
                          e.stopPropagation()

                          if (isAssigned) return

                          setSelectedClasses((prev) =>
                            prev.filter(
                              (selected) =>
                                (selected.id ?? selected.class_id) !==
                                (item.id ?? item.class_id)
                            )
                          )
                        }}
                      >
                        <IconX className='size-6' />
                      </button>
                    </Flex>
                    {index !==
                      assignedClasses.concat(selectedClasses).length - 1 && (
                      <div className='absolute bottom-[0px] left-[50%] w-full -translate-x-[50%] border-t-[1px] border-[#EAECF0]'></div>
                    )}
                  </div>
                )
              })}
            </Flex>
          )}
        </div>
        <DialogFooter className='mt-3 w-full gap-2'>
          <Button
            onClick={() => {
              handleBack()
            }}
            className='flex-1'
            variant='outline'
            disabled={isAddingClass}
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>

          <Button
            className='flex-1'
            type='button'
            onClick={handleNext}
            disabled={
              isAddingClass ||
              (assignedClassIds.length + selectedClasses.length === 0 &&
                step === 2)
            }
          >
            {isAddingClass ? (
              <Spinner />
            ) : (
              <>{step === 1 ? 'Add' : step === 2 ? 'Continue' : 'Save'}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AddClassModal
